/** Ambient creatures and weather: birds, rolling thunder, sand gusts, singing dunes. */
import { SILENCE, ahr, perc } from '../env';
import { clamp, nyquist } from '../util';
import { link, noiseSrc, type Synth } from './synth';

/**
 * A short procedural bird call: one FM sine carrier re-pitched per chirp (fast sweeps), a gentle
 * warble modulator, and a per-chirp envelope. Three species-ish shapes.
 */
export function birdCall(s: Synth, t: number, peak: number, pan: number): void {
  const rng = s.kit.rng;
  const ny = nyquist(s.kit.ctx);
  const v = s.pool.voice(t, s.route, { pan, send: 0.55 });
  const base = rng.range(2300, 3600);
  const car = v.osc('sine', base);
  const mod = v.osc('sine', rng.range(55, 95));
  const modAmt = v.gain(base * rng.range(0.02, 0.05));
  link(mod, modAmt);
  modAmt.connect(car.frequency);
  const g = v.gain();
  const hp = v.filter('highpass', 1200, 0.7);
  link(car, hp, g, v.out);
  const fr = car.frequency;
  const p = g.gain;
  p.setValueAtTime(0, t);
  const kind = rng.int(0, 2);
  let tt = t;
  const top = (f: number): number => clamp(f, 400, ny);
  if (kind === 0) {
    // Twittering up-chirps.
    const n = rng.int(2, 5);
    for (let i = 0; i < n; i++) {
      const f0 = base * rng.range(0.85, 1.0);
      fr.setValueAtTime(top(f0), tt);
      fr.exponentialRampToValueAtTime(top(f0 * 1.55), tt + 0.035);
      fr.exponentialRampToValueAtTime(top(f0 * 1.2), tt + 0.075);
      p.setValueAtTime(0, tt);
      p.linearRampToValueAtTime(peak, tt + 0.006);
      p.exponentialRampToValueAtTime(SILENCE, tt + 0.08);
      p.linearRampToValueAtTime(0, tt + 0.082);
      tt += rng.range(0.1, 0.16);
    }
  } else if (kind === 1) {
    // Descending trill.
    const n = rng.int(6, 10);
    for (let i = 0; i < n; i++) {
      const f0 = top(base * (1.25 - i * 0.04));
      fr.setValueAtTime(f0, tt);
      fr.exponentialRampToValueAtTime(top(f0 * 0.82), tt + 0.03);
      p.setValueAtTime(0, tt);
      p.linearRampToValueAtTime(peak * (1 - i * 0.05), tt + 0.004);
      p.exponentialRampToValueAtTime(SILENCE, tt + 0.034);
      p.linearRampToValueAtTime(0, tt + 0.036);
      tt += 0.042;
    }
  } else {
    // Two-tone "fee-bee" whistle.
    for (const r of [1.18, 0.94]) {
      const f0 = top(base * r * 0.8);
      fr.setValueAtTime(f0, tt);
      fr.linearRampToValueAtTime(top(f0 * 1.03), tt + 0.16);
      p.setValueAtTime(0, tt);
      p.linearRampToValueAtTime(peak * 0.8, tt + 0.03);
      p.setValueAtTime(peak * 0.8, tt + 0.13);
      p.exponentialRampToValueAtTime(SILENCE, tt + 0.19);
      p.linearRampToValueAtTime(0, tt + 0.192);
      tt += 0.24;
    }
  }
  const end = tt + 0.12;
  v.play(car, t, end);
  v.play(mod, t, end);
}

/** Rolling thunder: brown rumble with a scripted multi-bump envelope; a soft crack when close. */
export function thunder(s: Synth, t: number, peak: number, close: number, pan: number): void {
  const rng = s.kit.rng;
  const v = s.pool.voice(t, s.route, { pan, send: 0.45 });
  const lp = v.filter('lowpass', 160 + 240 * close, 0.7);
  const g = v.gain();
  const p = g.gain;
  p.setValueAtTime(0, t);
  let tt = t + rng.range(0.15, 0.3);
  p.linearRampToValueAtTime(peak * rng.range(0.5, 0.8), tt);
  const bumps = rng.int(2, 4);
  for (let i = 0; i < bumps; i++) {
    tt += rng.range(0.25, 0.5);
    p.linearRampToValueAtTime(peak * rng.range(0.3, 0.55), tt);
    tt += rng.range(0.12, 0.3);
    p.linearRampToValueAtTime(peak * rng.range(0.6, 1) * (1 - i * 0.15), tt);
  }
  const fadeEnd = tt + rng.range(1.2, 2);
  p.exponentialRampToValueAtTime(SILENCE, fadeEnd);
  p.linearRampToValueAtTime(0, fadeEnd + 0.01);
  const n = noiseSrc(v, s.kit, s.kit.noise.brown, t, fadeEnd + 0.05, rng.range(0.7, 1));
  link(n, lp, g, v.out);
  if (close > 0.55) {
    const c = noiseSrc(v, s.kit, s.kit.noise.pink, t, t + 0.6);
    const bp = v.filter('bandpass', 900, 0.5);
    const cg = v.gain();
    link(c, bp, cg, v.out);
    perc(cg.gain, t + 0.02, peak * 0.5 * close, 0.004, 0.45);
  }
}

/** A sand gust sweeping across: band-passed pink swell + hissing grains. */
export function sandGust(s: Synth, t: number, peak: number, dur: number, pan: number): void {
  const ny = nyquist(s.kit.ctx);
  const v = s.pool.voice(t, s.route, { pan, send: 0.15 });
  const end = t + dur + 0.1;
  const n = noiseSrc(v, s.kit, s.kit.noise.pink, t, end);
  const bp = v.filter('bandpass', 500, 0.9);
  bp.frequency.setValueAtTime(500, t);
  bp.frequency.exponentialRampToValueAtTime(Math.min(1700, ny), t + dur * 0.45);
  bp.frequency.exponentialRampToValueAtTime(650, t + dur);
  const g = v.gain();
  link(n, bp, g, v.out);
  ahr(g.gain, t, peak * 2.5, dur * 0.4, dur * 0.1, dur * 0.5);
  const c = noiseSrc(v, s.kit, s.kit.noise.crackle, t, end, 1.3);
  const hp = v.filter('highpass', 3800, 0.6);
  const cg = v.gain();
  link(c, hp, cg, v.out);
  ahr(cg.gain, t + dur * 0.1, peak * 2.4, dur * 0.35, dur * 0.1, dur * 0.45);
}

/** "Singing dune": the low booming hum of avalanching sand, with a slow vibrato. */
export function singingDune(s: Synth, t: number, peak: number, hz: number): void {
  const v = s.pool.voice(t, s.route, { send: 0.4, pan: s.kit.rng.bi() * 0.4 });
  const lp = v.filter('lowpass', 600, 0.6);
  lp.connect(v.out);
  const g = v.gain();
  g.connect(lp);
  const vib = v.osc('sine', 4.2);
  const end = ahr(g.gain, t, peak, 1.8, 1.2, 2.6);
  for (const [r, a] of [
    [1, 1],
    [2, 0.4],
    [3, 0.15],
  ] as const) {
    const osc = v.osc('triangle', hz * r);
    const og = v.gain(a);
    const vAmt = v.gain(hz * r * 0.008);
    link(vib, vAmt);
    vAmt.connect(osc.frequency);
    link(osc, og, g);
    v.play(osc, t, end);
  }
  v.play(vib, t, end);
}
